import type { CSSProperties } from 'react';
import { initialsOf, showsRsvpBadge, t, type Attending, type AvatarPerson } from '@bms2/shared';
import { Icon } from '../Icon';
import styles from './AttendeeAvatar.module.css';

/**
 * Attendee Avatar (piece 4, decision 0055): a circular initials badge taking
 * `person` and `attending` and nothing else; never interactive. The fill is
 * `PersonRecord.avatarColor`, a palette token name, drawn as given and never
 * derived here. A row spaces its circles centre to centre at the
 * `avatar-row-spacing-ratio` token, 2.4 radii, so the edges sit 0.4 radii
 * apart and nothing overlaps.
 */
export const attendeeAvatarDocumentedStates = ['needsAction', 'accepted', 'declined', 'tentative', 'row'] as const;

export interface AttendeeAvatarProps {
  person: AvatarPerson;
  attending: Attending;
}

const attendingKey = {
  NeedsAction: 'avatar.needsAction',
  Accepted: 'avatar.accepted',
  Declined: 'avatar.declined',
  Tentative: 'avatar.tentative',
} as const;

function avatarLabel(person: AvatarPerson, attending: Attending): string {
  return `${person.displayName}, ${t(attendingKey[attending])}`;
}

export function AttendeeAvatar({ person, attending }: AttendeeAvatarProps) {
  const initials = initialsOf(person.displayName);
  const style = { '--avatar-fill': `var(--${person.avatarColor})` } as CSSProperties;
  return (
    <span className={styles.avatar} style={style} role="img" aria-label={avatarLabel(person, attending)} data-avatar={person.avatarColor} data-attending={attending}>
      <span className={styles.initials} aria-hidden="true">
        {initials}
      </span>
      {showsRsvpBadge(attending) ? (
        <span className={styles.badge} data-rsvp-badge="true" aria-hidden="true">
          <Icon meaning="confirmed" size="inline" />
        </span>
      ) : null}
    </span>
  );
}

export interface AvatarRowProps {
  attendees: readonly AttendeeAvatarProps[];
}

/** Separate circles on the token spacing; the component implements no overlap. */
export function AvatarRow({ attendees }: AvatarRowProps) {
  return (
    <span className={styles.row} data-avatar-row="true">
      {attendees.map((a, i) => (
        <AttendeeAvatar key={`${a.person.displayName}-${i}`} person={a.person} attending={a.attending} />
      ))}
    </span>
  );
}
